import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Upload, ScanLine, Loader2, FileText, X, TrendingUp,
  Users, Coins, Scale, ArrowRight, Info, Zap,
} from "lucide-react";

function money(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `$${Math.round(Number(v)).toLocaleString()}`;
}
function pct(dec) {
  if (dec == null || !Number.isFinite(dec)) return "—";
  return `${(dec * 100).toFixed(1)}%`;
}

export default function AdminNmiAnalysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [result, setResult] = useState(null);
  const [inputs, setInputs] = useState({
    grid_rate_cents: 36,
    ppa_rate_cents: 18.5,
    capital_per_kw: 1500,
    capital_per_kwh_battery: 750,
    fixed_capital: 15000,
    maintenance_pct_of_capital: 0.015,
    term_years: 10,
    investor_irr_floor: 0.12,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/admin/applications/${id}`);
        setApp(data);
        // Try to prefill grid rate from bill hint
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  async function handleUpload(e) {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "nmi_interval");
      const { data } = await api.post("/api/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(data);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function runAnalysis() {
    if (!file) return toast.error("Please upload interval data first");
    setAnalysing(true);
    setResult(null);
    try {
      const { data } = await api.post("/api/admin/nmi-analysis", {
        file_id: file.file_id,
        application_id: id,
        ...inputs,
      });
      setResult(data.result);
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAnalysing(false);
    }
  }

  async function applyToProposal(rec) {
    // Create or update a proposal for this application with the recommended sizing
    try {
      const payload = {
        application_id: id,
        business_name: (app?.data?.legal_business_name) || "",
        site_address: `${app?.data?.site_address || ""} ${app?.data?.site_suburb || ""} ${app?.data?.site_state || ""} ${app?.data?.site_postcode || ""}`.trim(),
        current_annual_kwh: result.annual_load_kwh,
        current_avg_rate_cents: inputs.grid_rate_cents,
        current_annual_cost: Math.round(result.annual_load_kwh * inputs.grid_rate_cents / 100),
        system_size_kw: rec.solar_kw,
        battery_size_kwh: rec.battery_kwh,
        annual_generation_kwh: Math.round(rec.solar_kw * 1500),
        estimated_energy_supplied_kwh: rec.supplied_kwh,
        proposed_ppa_rate_cents: inputs.ppa_rate_cents,
        proposed_term_years: inputs.term_years,
        proposed_annual_cost: Math.round(rec.supplied_kwh * inputs.ppa_rate_cents / 100 + (result.annual_load_kwh - rec.supplied_kwh) * inputs.grid_rate_cents / 100),
        estimated_annual_savings: rec.annual_savings,
        estimated_co2_reduction_tonnes: Number((rec.supplied_kwh * 0.7 / 1000).toFixed(1)),
        assumptions: `Sizing derived from NMI interval-data analysis (${result.interval_count} intervals covering ${result.period.start?.slice(0,10)} - ${result.period.end?.slice(0,10)}). Balanced recommendation targeting >= ${(inputs.investor_irr_floor*100).toFixed(0)}% investor IRR.`,
        capital_cost: rec.capital,
        annual_maintenance_cost: rec.annual_maintenance,
      };
      const { data } = await api.post("/api/admin/proposals", payload);
      toast.success("Proposal created — opening builder");
      navigate(`/admin/applications/${id}/proposal?pid=${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div data-testid="admin-nmi-analysis">
      <button onClick={() => navigate(`/admin/applications/${id}`)} className="inline-flex items-center gap-2 text-forest text-[13.5px] font-semibold mb-4">
        <ArrowLeft size={14}/> Back to application
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-2 flex items-center gap-2"><ScanLine size={13} className="text-bright"/> NMI Data Analysis</p>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-forest">Deep sizing analysis from 30-minute interval data</h1>
          <p className="text-neutral-500 text-[13.5px] mt-1 max-w-3xl">
            Upload the customer&apos;s NMI interval data (NEM12 CSV, or Excel/CSV with datetime + kWh columns). We simulate solar + battery dispatch for every half-hour and search a sizing grid to find the customer-best, investor-best and balanced recommendations.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        {/* LEFT — Inputs */}
        <section className="rounded-2xl bg-white border border-black/5 p-6 space-y-5">
          <h3 className="font-heading font-semibold text-forest">Interval data</h3>
          <label className="rounded-2xl border-2 border-dashed border-black/10 hover:border-forest/40 hover:bg-mist/60 transition-colors p-5 cursor-pointer flex items-center gap-3 bg-white block">
            <span className="h-10 w-10 rounded-xl bg-mist grid place-items-center text-forest">
              {uploading ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-semibold text-forest">Upload NEM12 or CSV/XLSX</p>
              <p className="text-[11.5px] text-neutral-500 mt-0.5">Two columns: datetime, kWh (30-min preferred)</p>
            </div>
            <input type="file" className="hidden" accept=".csv,.txt,.xlsx,.xls" onChange={handleUpload} data-testid="nmi-upload"/>
          </label>
          {file && (
            <div className="flex items-center gap-2 bg-mist border border-black/5 rounded-xl p-3" data-testid="nmi-file">
              <FileText size={14} className="text-forest"/>
              <span className="text-[13px] flex-1 truncate">{file.original_filename}</span>
              <button onClick={() => setFile(null)} aria-label="Remove"><X size={13}/></button>
            </div>
          )}

          <h3 className="font-heading font-semibold text-forest pt-2">Rates &amp; economics</h3>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Grid rate (c/kWh)" v={inputs.grid_rate_cents} on={(v) => setInputs({...inputs, grid_rate_cents: v})} tid="nmi-grid-rate"/>
            <NumField label="PPA rate (c/kWh)" v={inputs.ppa_rate_cents} on={(v) => setInputs({...inputs, ppa_rate_cents: v})} tid="nmi-ppa-rate"/>
            <NumField label="Capital $/kW solar" v={inputs.capital_per_kw} on={(v) => setInputs({...inputs, capital_per_kw: v})} tid="nmi-cap-kw"/>
            <NumField label="Capital $/kWh battery" v={inputs.capital_per_kwh_battery} on={(v) => setInputs({...inputs, capital_per_kwh_battery: v})} tid="nmi-cap-batt"/>
            <NumField label="Fixed capital ($)" v={inputs.fixed_capital} on={(v) => setInputs({...inputs, fixed_capital: v})} tid="nmi-fixed-cap"/>
            <NumField label="Term (years)" v={inputs.term_years} on={(v) => setInputs({...inputs, term_years: v})} tid="nmi-term"/>
            <NumField label="Maint. % of capital" step="0.001" v={inputs.maintenance_pct_of_capital} on={(v) => setInputs({...inputs, maintenance_pct_of_capital: v})} tid="nmi-maint"/>
            <NumField label="Investor IRR floor" step="0.01" v={inputs.investor_irr_floor} on={(v) => setInputs({...inputs, investor_irr_floor: v})} tid="nmi-irr-floor"/>
          </div>

          <Button
            onClick={runAnalysis}
            disabled={!file || analysing}
            data-testid="nmi-run"
            className="w-full rounded-full h-11 bg-bright hover:bg-bright/90 text-[hsl(150_40%_8%)] font-semibold"
          >
            {analysing ? <><Loader2 size={15} className="animate-spin mr-1.5"/> Running dispatch simulation…</> : <><ScanLine size={15} className="mr-1.5"/> Run NMI analysis</>}
          </Button>
          <div className="rounded-xl bg-mist border border-black/5 p-3 text-[12px] text-neutral-600 flex items-start gap-2">
            <Info size={13} className="text-forest mt-0.5"/>
            The analyser searches ~20 (solar,battery) combinations, simulating dispatch across all your intervals. Analysis takes 10-30 seconds for a full year of half-hour data.
          </div>
        </section>

        {/* RIGHT — Results */}
        <section className="space-y-5" data-testid="nmi-results">
          {!result && !analysing && (
            <div className="rounded-2xl bg-mist border border-black/5 p-10 text-center text-neutral-500">
              <ScanLine size={22} className="mx-auto text-bright mb-2"/>
              Upload interval data and click <strong>Run NMI analysis</strong> to see recommendations.
            </div>
          )}
          {analysing && (
            <div className="rounded-2xl bg-mist border border-black/5 p-10 text-center text-neutral-500">
              <Loader2 size={22} className="mx-auto animate-spin mb-2 text-forest"/>
              Simulating half-hourly dispatch across the interval dataset…
            </div>
          )}
          {result && (
            <>
              <div className="rounded-2xl bg-white border border-black/5 p-5">
                <div className="grid grid-cols-3 gap-3">
                  <Tile icon={Zap} label="Annual load" value={`${Math.round(result.annual_load_kwh).toLocaleString()} kWh`}/>
                  <Tile icon={ScanLine} label="Intervals analysed" value={result.interval_count.toLocaleString()}/>
                  <Tile icon={Info} label="Period" value={`${result.period.start?.slice(0,10)} - ${result.period.end?.slice(0,10)}`}/>
                </div>
              </div>

              <RecommendationCard title="Customer-best" tone="mist" icon={Users}
                                  rec={result.recommendations.customer_best} inputs={inputs} onApply={applyToProposal} testId="rec-customer"/>
              <RecommendationCard title="Balanced (recommended)" tone="bright" icon={Scale}
                                  rec={result.recommendations.balanced} inputs={inputs} onApply={applyToProposal} testId="rec-balanced" primary/>
              <RecommendationCard title="Investor-best" tone="mist" icon={Coins}
                                  rec={result.recommendations.investor_best} inputs={inputs} onApply={applyToProposal} testId="rec-investor"/>

              <details className="rounded-2xl bg-mist border border-black/5">
                <summary className="cursor-pointer p-4 font-heading font-semibold text-forest text-[14px]" data-testid="all-scenarios-toggle">All {result.scenarios.length} scenarios evaluated</summary>
                <div className="overflow-x-auto border-t border-black/5">
                  <table className="min-w-[720px] w-full text-[12.5px]">
                    <thead className="bg-white/70 text-[11px] uppercase tracking-widest text-neutral-500">
                      <tr><Th>Solar</Th><Th>Battery</Th><Th>Supplied</Th><Th>Self-suff</Th><Th>Savings/yr</Th><Th>Capital</Th><Th>Investor IRR</Th></tr>
                    </thead>
                    <tbody>
                      {result.scenarios.map((s, i) => (
                        <tr key={i} className="border-t border-black/5">
                          <Td>{s.solar_kw} kW</Td>
                          <Td>{s.battery_kwh} kWh</Td>
                          <Td>{Math.round(s.supplied_kwh).toLocaleString()} kWh</Td>
                          <Td>{s.self_sufficiency_pct}%</Td>
                          <Td className="text-emerald-700 font-semibold">{money(s.annual_savings)}</Td>
                          <Td>{money(s.capital)}</Td>
                          <Td className="font-semibold">{pct(s.investor_irr)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function RecommendationCard({ title, rec, tone, icon: Icon, onApply, inputs, testId, primary }) {
  const bright = tone === "bright";
  return (
    <div className={`rounded-2xl border p-5 md:p-6 ${bright ? "bg-bright/10 border-bright/40" : "bg-white border-black/5"}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-forest text-white"><Icon size={15}/></span>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-forest/70 font-semibold">Recommendation</p>
            <p className="font-heading font-semibold text-forest text-[16px] leading-tight">{title}</p>
          </div>
        </div>
        <Button onClick={() => onApply(rec)} data-testid={`${testId}-apply`} className={`rounded-full h-9 px-4 font-semibold ${primary ? "bg-forest hover:bg-forest/90 text-white" : "bg-white text-forest border border-forest/20 hover:bg-mist"}`}>
          Apply to proposal <ArrowRight size={13} className="ml-1"/>
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Mini label="Solar" value={`${rec.solar_kw} kW`}/>
        <Mini label="Battery" value={`${rec.battery_kwh} kWh`}/>
        <Mini label="Self-sufficiency" value={`${rec.self_sufficiency_pct}%`}/>
        <Mini label="Supplied / yr" value={`${Math.round(rec.supplied_kwh).toLocaleString()} kWh`}/>
        <Mini label="Annual savings" value={money(rec.annual_savings)} tone="green"/>
        <Mini label="Capital" value={money(rec.capital)}/>
        <Mini label="Investor IRR" value={pct(rec.investor_irr)}/>
        <Mini label="Maintenance /yr" value={money(rec.annual_maintenance)}/>
      </div>
    </div>
  );
}

function Mini({ label, value, tone }) {
  return (
    <div className={`rounded-lg p-3 ${tone === "green" ? "bg-emerald-50 border border-emerald-100" : "bg-mist border border-black/5"}`}>
      <p className="text-[10px] uppercase tracking-widest text-forest/60 font-semibold">{label}</p>
      <p className={`mt-1 font-heading font-semibold text-[14.5px] ${tone === "green" ? "text-emerald-800" : "text-forest"}`}>{value}</p>
    </div>
  );
}
function Tile({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <Icon size={16} className="mx-auto text-forest"/>
      <p className="text-[10.5px] uppercase tracking-widest text-forest/70 font-semibold mt-1.5">{label}</p>
      <p className="font-heading font-semibold text-forest text-[15px] mt-0.5">{value}</p>
    </div>
  );
}
function NumField({ label, v, on, step = "any", tid }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-semibold text-forest">{label}</span>
      <Input type="number" step={step} value={v ?? ""} onChange={(e) => on(e.target.value === "" ? "" : Number(e.target.value))} data-testid={tid} className="mt-1"/>
    </label>
  );
}
function Th({ children }) { return <th className="text-left px-3 py-2 font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 ${className}`}>{children}</td>; }
